/**
 * main.js - rebuilt from scratch 2026-04-24
 *
 * Clean, readable, DRM-free reimplementation of the custom elements this
 * theme depends on. Based on Shopify Dawn (open source, MIT license) with
 * adaptations for the kyltic/Shrine-derived theme.
 *
 * Classes included:
 *   - Utilities: debounce, throttle, fetchConfig, subscribe/publish, pub-sub events
 *   - CartRemoveButton
 *   - CartItems (+ CartDrawerItems subclass)
 *   - CartNote
 *   - CartDrawer (+ CartNotification fallback)
 *   - ProductForm (intercepts submit, uses /cart/add.js, opens drawer)
 *   - QuantityInput
 *   - VariantSelects (+ thin subclasses used by the theme)
 *   - StickyAtc (minimal)
 *
 * Stubs (defined as empty HTMLElement so the theme's liquid doesn't error):
 *   bundle-deals, quantity-breaks, quantity-gifts, product-info,
 *   product-info-upsell, cart-drawer-upsell, cart-drawer-gift,
 *   main-bundle-offer, custom-product-field, internal-video,
 *   comparison-slider, promo-popup, section-group, clickable-discount,
 *   dynamic-dates, search-form, predictive-search,
 *   secondary-variant-select, secondary-variant-select-separate,
 *   card-variant-selects.
 */

// =====================================================================
// CRITICAL GLOBAL UTILITIES - MUST be defined BEFORE any custom element
// secondary.js HeaderDrawer/ModalDialog/ProductModal reference these as globals
// =====================================================================

const trapFocusHandlers = {};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (!last) return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus && typeof elementToFocus.focus === 'function') {
    try { elementToFocus.focus(); } catch (e) {}
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus && typeof elementToFocus.focus === 'function') {
    try { elementToFocus.focus(); } catch (e) {}
  }
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    try { video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'); } catch (e) {}
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    try { video.contentWindow.postMessage('{"method":"pause"}', '*'); } catch (e) {}
  });
  document.querySelectorAll('video').forEach((video) => {
    try { video.pause(); } catch (e) {}
  });
  document.querySelectorAll('product-model').forEach((model) => {
    try { if (model.modelViewerUI) model.modelViewerUI.pause(); } catch (e) {}
  });
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  if (summaryElement) {
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
  }
}

// Expose utilities on window IMMEDIATELY
window.trapFocus = trapFocus;
window.removeTrapFocus = removeTrapFocus;
window.getFocusableElements = getFocusableElements;
window.pauseAllMedia = pauseAllMedia;
window.onKeyUpEscape = onKeyUpEscape;



/* =========================================================================
 * 1. UTILITIES
 * ========================================================================= */

const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

let subscribers = {};

function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) subscribers[eventName] = [];
  subscribers[eventName] = [...subscribers[eventName], callback];
  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter((cb) => cb !== callback);
  };
}

function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((cb) => {
      try { cb(data); } catch (e) { console.error(e); }
    });
  }
}

function debounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let last = 0, queued;
  return function (...args) {
    const now = Date.now();
    if (now - last >= delay) {
      last = now;
      fn.apply(this, args);
    } else {
      clearTimeout(queued);
      queued = setTimeout(() => {
        last = Date.now();
        fn.apply(this, args);
      }, delay - (now - last));
    }
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

function formatMoney(cents) {
  try {
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
  } catch (e) {}
  return (cents / 100).toFixed(2);
}

function formatDates(currentDate, dateValue) {
  return document.getElementById('shopping-cart-line-item-status') ||
         document.getElementById('CartDrawer-LineItemStatus');
}

/* =========================================================================
 * 2. CART - Remove button, Cart items, Cart note
 * ========================================================================= */

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (evt) => {
      evt.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (this.dataset.clearCart !== undefined || this.classList.contains('clear-cart')) {
        if (cartItems) cartItems.clearCart();
      } else if (cartItems) {
        cartItems.updateQuantity(this.dataset.index, 0);
      }
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    fetch(`${window.Shopify.routes.root || '/'}?section_id=main-cart-items`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty && this.querySelector('.js-contents')) {
          this.querySelector('.js-contents').innerHTML = sourceQty.querySelector('.js-contents').innerHTML;
        }
      })
      .catch(console.error);
  }

  getSectionsToRender() {
    return [
      { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id, selector: '.js-contents' },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((s) => s.section).filter(Boolean),
      sections_url: window.location.pathname,
    });

    fetch(`${window.Shopify.routes.root || '/'}cart/change.js`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const el = document.getElementById(section.id);
          if (!el || !parsedState.sections?.[section.section]) return;
          const newContent = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          if (newContent) el.innerHTML = newContent;
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
      })
      .catch((err) => console.error(err))
      .finally(() => this.disableLoading(line));
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');
    const loading = this.querySelectorAll(`#CartItem-${line} .loading__spinner, #CartDrawer-Item-${line} .loading__spinner`);
    loading.forEach((el) => el.classList.remove('hidden'));
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');
    const loading = this.querySelectorAll(`#CartItem-${line} .loading__spinner, #CartDrawer-Item-${line} .loading__spinner`);
    loading.forEach((el) => el.classList.add('hidden'));
  }

  clearCart() {
    fetch(`${window.Shopify.routes.root || '/'}cart/clear.js`, { method: 'POST' })
      .then(() => {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
        this.onCartUpdate();
      })
      .catch(console.error);
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        const debouncedOnChange = debounce((event) => {
          const body = JSON.stringify({ note: event.target.value });
          fetch(`${window.Shopify.routes.root || '/'}cart/update.js`, { ...fetchConfig(), body }).catch(console.error);
        }, ON_CHANGE_DEBOUNCE_TIMER);
        this.addEventListener('change', debouncedOnChange);
      }
    }
  );
}

/* =========================================================================
 * 3. CART DRAWER
 * ========================================================================= */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code && event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // Ensure drawer transition runs
    this.classList.add('animate', 'active');
    this.addEventListener('transitionend', () => {
      const container = this.querySelector('#CartDrawer');
      if (container) container.focus();
    }, { once: true });
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    if (this.activeElement && typeof this.activeElement.focus === 'function') {
      try { this.activeElement.focus(); } catch (e) {}
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    // Refetch the full cart-drawer section and swap it cleanly
    const root = window.Shopify.routes.root || '/';
    fetch(`${root}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('cart-drawer');
        if (newDrawer) {
          this.innerHTML = newDrawer.innerHTML;
          this.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
        }
      })
      .then(() => fetch(`${root}?section_id=cart-icon-bubble`))
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newBubble = doc.getElementById('cart-icon-bubble');
        const bubble = document.getElementById('cart-icon-bubble');
        if (newBubble && bubble) bubble.innerHTML = newBubble.innerHTML;
      })
      .then(() => {
        setTimeout(() => this.open(), 10);
      })
      .catch((err) => console.error('renderContents error', err));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }
}
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // Override: simpler refresh - refetch the full cart-drawer section and swap innerHTML
  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({ line, quantity });
    const root = window.Shopify.routes.root || '/';

    fetch(`${root}cart/change.js`, { ...fetchConfig(), body })
      .then((r) => r.json())
      .then((parsedState) => {
        const drawerEl = document.querySelector('cart-drawer');
        if (drawerEl) drawerEl.classList.toggle('is-empty', parsedState.item_count === 0);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
        return this.refreshDrawerAndBubble();
      })
      .catch((err) => console.error('cart change error', err))
      .finally(() => this.disableLoading(line));
  }

  onCartUpdate() {
    this.refreshDrawerAndBubble();
  }

  refreshDrawerAndBubble() {
    const root = window.Shopify.routes.root || '/';
    const drawer = document.querySelector('cart-drawer');

    return fetch(`${root}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('cart-drawer');
        if (drawer && newDrawer) {
          drawer.innerHTML = newDrawer.innerHTML;
          drawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
        }
      })
      .then(() => fetch(`${root}?section_id=cart-icon-bubble`))
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newBubble = doc.getElementById('cart-icon-bubble');
        const bubble = document.getElementById('cart-icon-bubble');
        if (newBubble && bubble) bubble.innerHTML = newBubble.innerHTML;
      })
      .catch((err) => console.error('drawer refresh error', err));
  }

  enableLoading(line) {
    const container = this.closest('cart-drawer') || this;
    if (container) container.classList.add('is-loading');
    const loaders = this.querySelectorAll(`[data-line="${line}"] .loading__spinner, [data-line="${line}"] .loading-overlay__spinner`);
    loaders.forEach((el) => el.classList.remove('hidden'));
  }

  disableLoading(line) {
    const container = this.closest('cart-drawer') || this;
    if (container) container.classList.remove('is-loading');
    const loaders = this.querySelectorAll(`[data-line="${line}"] .loading__spinner, [data-line="${line}"] .loading-overlay__spinner`);
    loaders.forEach((el) => el.classList.add('hidden'));
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);

/* Fallback cart-notification (used when cart_type != 'drawer') */
if (!customElements.get('cart-notification')) {
  customElements.define(
    'cart-notification',
    class CartNotification extends HTMLElement {
      constructor() {
        super();
        this.notification = document.getElementById('cart-notification');
        this.header = document.querySelector('sticky-header');
        this.onBodyClick = this.handleBodyClick.bind(this);
        if (this.notification) {
          this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
          const closeBtn = this.querySelector('#cart-notification-button');
          if (closeBtn) closeBtn.addEventListener('click', () => this.close());
        }
      }
      open() {
        if (!this.notification) return;
        this.notification.classList.add('animate', 'active');
        this.notification.addEventListener('transitionend', () => {
          this.notification.focus();
          setTimeout(() => document.body.addEventListener('click', this.onBodyClick));
        }, { once: true });
      }
      close() {
        if (!this.notification) return;
        this.notification.classList.remove('active');
        document.body.removeEventListener('click', this.onBodyClick);
      }
      handleBodyClick(evt) {
        if (this.notification && !this.notification.contains(evt.target)) this.close();
      }
      renderContents(parsed) {
        this.productId = parsed.id;
        this.open();
      }
    }
  );
}

/* =========================================================================
 * 4. PRODUCT FORM (intercepts submit ? AJAX /cart/add.js ? open drawer)
 * ========================================================================= */

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.product-form__error-message');
        this.loadingSpinner = this.submitButton ? this.submitButton.querySelector('.loading-overlay__spinner, .loading__spinner') : null;
        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        if (this.loadingSpinner) this.loadingSpinner.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((s) => s.id).join(',')
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement && this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${window.Shopify.routes.root || '/'}cart/add.js`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: formData.get('id'), errors: response.errors || response.description, message: response.message });
              this.handleErrorMessage(response.description || response.message || 'Error');
              const soldOut = this.submitButton.querySelector('.sold-out-message');
              if (soldOut) {
                this.submitButton.querySelector('.btn__text')?.classList.add('hidden');
                soldOut.classList.remove('hidden');
                this.submitButton.setAttribute('aria-disabled', 'true');
              }
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });

            if (this.cart && typeof this.cart.renderContents === 'function') {
              this.cart.renderContents(response);
            } else {
              // Fallback: no drawer, redirect to cart
              window.location = `${window.Shopify.routes.root || '/'}cart`;
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage('Es ist ein Fehler aufgetreten. Bitte versuche es erneut.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.loadingSpinner) this.loadingSpinner.classList.add('hidden');
            this.submitButton.removeAttribute('aria-disabled');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
          if (errorMessage && this.errorMessage) this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

/* =========================================================================
 * 5. QUANTITY INPUT (native +/- controls)
 * ========================================================================= */

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;
  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }
  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    const name = event.currentTarget.name || '';
    if (name === 'plus' || event.currentTarget.classList.contains('plus') || event.currentTarget.dataset.action === 'increase') {
      this.input.stepUp();
    } else {
      this.input.stepDown();
    }
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus'], .quantity__button.minus");
      if (buttonMinus) buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus'], .quantity__button.plus");
      if (buttonPlus) buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}
customElements.define('quantity-input', QuantityInput);

/* =========================================================================
 * 6. VARIANT SELECTS (Dawn-style, minimal)
 * ========================================================================= */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => this.options[index] === option).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;
    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    // No-op - full implementation depends on theme-specific option trees
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;
    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;
    const errorMessage = section.querySelector('.product-form__error-message-wrapper');
    if (errorMessage) errorMessage.toggleAttribute('hidden', true);
  }

  renderProductInfo() {
    // Simplified: refetch the product section on variant change
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
    fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        if (this.currentVariant.id !== requestedVariantId) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(`price-${this.dataset.originalSection || this.dataset.section}`);
        const skuSource = html.getElementById(`Sku-${this.dataset.originalSection || this.dataset.section}`);
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(`Inventory-${this.dataset.originalSection || this.dataset.section}`);
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);
        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
        }
        const priceContainer = document.getElementById(`price-${this.dataset.section}`);
        if (priceContainer) priceContainer.classList.remove('visibility-hidden');
        this.toggleAddButton(!this.currentVariant.available, window.variantStrings ? window.variantStrings.soldOut : 'Sold out');
        publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.dataset.section, html, variant: this.currentVariant } });
      })
      .catch(console.error);
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;
    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text && addButtonText) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      if (addButtonText) addButtonText.textContent = window.variantStrings ? window.variantStrings.addToCart : 'Add to cart';
    }
    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button ? button.querySelector('[name="add"]') : null;
    const addButtonText = button ? button.querySelector('[name="add"] > span') : null;
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    if (!addButton) return;
    if (addButtonText) addButtonText.textContent = window.variantStrings ? window.variantStrings.unavailable : 'Unavailable';
    if (price) price.classList.add('visibility-hidden');
    if (inventory) inventory.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}
customElements.define('variant-selects', VariantSelects);

class SecondaryVariantSelect extends VariantSelects {
  constructor() {
    super();
    this.isSecondary = true;
  }
  updateOptions() {
    const select = this.querySelector('select');
    if (select) this.options = select.value.split(',');
  }
}
customElements.define('secondary-variant-select', SecondaryVariantSelect);

class SecondaryVariantSelectSeparate extends VariantSelects {
  constructor() {
    super();
    this.isSecondary = true;
  }
}
customElements.define('secondary-variant-select-separate', SecondaryVariantSelectSeparate);

class CardVariantSelects extends VariantSelects {
  constructor() {
    super();
    this.quickAddSubmit = document.getElementById(this.dataset.section + '-submit');
  }
  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media) return;
    const imgEl = document.getElementById(this.dataset.section + '-image');
    if (!imgEl) return;
    const pv = this.currentVariant.featured_media.preview_image;
    if (!pv) return;
    const base = pv.src.split('?')[0];
    const widths = [165, 360, 533, 720, 940, 1066];
    const srcset = widths.filter((w) => pv.width >= w).map((w) => `${base}?width=${w} ${w}w`).join(', ');
    imgEl.srcset = srcset + `, ${base} ${pv.width}w`;
    imgEl.src = base + '?width=533';
  }
  updateQuickAddUrl() {
    if (!this.currentVariant || !this.quickAddSubmit) return;
    this.quickAddSubmit.dataset.productUrl = this.dataset.url + '?variant=' + this.currentVariant.id;
  }
}
customElements.define('card-variant-selects', CardVariantSelects);

/* =========================================================================
 * 7. STICKY ATC (minimal)
 * ========================================================================= */

class StickyAtc extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.dataset.trigger || '.product-form__submit';
    this.target = document.querySelector(this.trigger);
    this.visibilityThreshold = 0.1;
    this.onScroll = throttle(this.checkVisibility.bind(this), 50);
  }
  connectedCallback() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.checkVisibility();
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }
  checkVisibility() {
    if (!this.target) {
      this.classList.remove('is-visible');
      return;
    }
    const rect = this.target.getBoundingClientRect();
    const isVisible = rect.bottom < 0 || rect.top > window.innerHeight;
    this.classList.toggle('is-visible', isVisible);
  }
}
customElements.define('sticky-atc', StickyAtc);

/* =========================================================================
 * 8. STUBS - define empty custom elements so the theme's liquid doesn't error
 * ========================================================================= */



/* Search form - open/close behavior (predictive-search extends this) */
class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
    }
  }
  getQuery() { return this.input ? this.input.value.trim() : ''; }
  onChange() { if (this.resetButton) this.resetButton.classList.toggle('hidden', this.getQuery().length === 0); }
  shouldResetForm() { return !document.querySelector('[aria-selected="true"] a'); }
  onFormReset(event) {
    event.preventDefault();
    if (this.shouldResetForm()) {
      if (this.input) { this.input.value = ''; if (this.resetButton) this.resetButton.classList.add('hidden'); this.input.focus(); }
    }
  }
}
if (!customElements.get('search-form')) customElements.define('search-form', SearchForm);

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }
  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this));
    if (this.input) {
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('blur', this.onBlur.bind(this));
      this.input.addEventListener('keyup', this.onKeyup.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
    }
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }
  onFormSubmit(event) { if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault(); }
  onFormReset(event) { super.onFormReset(event); if (super.shouldResetForm()) { this.searchTerm = ''; this.abortController.abort(); this.close(true); } }
  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {}
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) this.close(true);
  }
  onFocus() { if (this.getQuery().length) this.open(); }
  onBlur() { this.classList.remove('focused'); }
  onKeyup(event) {
    if (event.code === 'Escape') this.close(true);
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {}
  }
  onKeydown(event) { if (event.code === 'Escape') this.close(true); }
  onFocusOut() { setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); }, 100); }
  open() { this.setAttribute('open', true); this.input && this.input.setAttribute('aria-expanded', true); this.isOpen = true; }
  close(clearSearchTerm = false) {
    if (clearSearchTerm) { if (this.input) this.input.value = ''; this.removeAttribute('results'); }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    if (this.input) { this.input.setAttribute('aria-activedescendant', ''); this.input.setAttribute('aria-expanded', false); }
    this.removeAttribute('loading');
    this.removeAttribute('open');
    if (this.input) this.input.setAttribute('aria-expanded', false);
    this.isOpen = false;
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

/*
 * Stubs supprim�s pour �viter conflict avec menu-drawer, localization-form,
 * etc. qui sont d�finis dans secondary.js / scripts.js. Les autres custom
 * elements non-d�finis seront trait�s par le navigateur comme des
 * HTMLUnknownElement (pas de crash, juste pas de comportement custom).
 * Si un custom element sp�cifique pose probl�me, l'impl�menter explicitement.
 */

/* =========================================================================
 * 9. GLOBAL EXPORTS (for backward compatibility with theme inline scripts)
 * ========================================================================= */

window.Shopify = window.Shopify || {};
window.Shopify.routes = window.Shopify.routes || { root: '/' };

// Some theme inline scripts expect these to be globals
window.debounce = debounce;
window.throttle = throttle;
window.fetchConfig = fetchConfig;
window.subscribe = subscribe;
window.publish = publish;
window.PUB_SUB_EVENTS = PUB_SUB_EVENTS;

/* ==========================================================================
 * FALLBACK CUSTOM ELEMENTS - only activate if secondary.js fails to define
 * (executes on DOMContentLoaded to give secondary.js a chance first)
 * ========================================================================== */

document.addEventListener('DOMContentLoaded', function () {
  // Menu drawer fallback: click summary opens details, escape closes
  if (!customElements.get('menu-drawer')) {
    customElements.define('menu-drawer', class MenuDrawer extends HTMLElement {
      constructor() {
        super();
        this.mainDetailsToggle = this.querySelector('details');
        if (!this.mainDetailsToggle) return;
        this.bindEvents();
      }
      bindEvents() {
        this.querySelectorAll('summary').forEach((summary) => {
          summary.setAttribute('role', 'button');
          summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));
          summary.addEventListener('click', this.onSummaryClick.bind(this));
        });
        this.addEventListener('keyup', (e) => {
          if (e.code === 'Escape') this.closeMenuDrawer(e, this.mainDetailsToggle.querySelector('summary'));
        });
      }
      onSummaryClick(event) {
        const summary = event.currentTarget;
        const detailsEl = summary.parentNode;
        const isOpen = detailsEl.hasAttribute('open');
        if (detailsEl === this.mainDetailsToggle) {
          if (isOpen) event.preventDefault();
          isOpen ? this.closeMenuDrawer(event, summary) : this.openMenuDrawer(summary);
        }
      }
      openMenuDrawer(summary) {
        setTimeout(() => this.mainDetailsToggle.classList.add('menu-opening'));
        summary.setAttribute('aria-expanded', true);
        document.body.classList.add('overflow-hidden');
      }
      closeMenuDrawer(event, elementToFocus = false) {
        if (event === undefined) return;
        this.mainDetailsToggle.classList.remove('menu-opening');
        if (elementToFocus) {
          elementToFocus.setAttribute('aria-expanded', false);
          this.mainDetailsToggle.removeAttribute('open');
          elementToFocus.focus();
        }
        document.body.classList.remove('overflow-hidden');
      }
    });
  }

  // Header drawer extends menu-drawer
  if (!customElements.get('header-drawer')) {
    const BaseMenuDrawer = customElements.get('menu-drawer');
    if (BaseMenuDrawer) {
      customElements.define('header-drawer', class HeaderDrawer extends BaseMenuDrawer {});
    }
  }

  // Details disclosure (simple dropdown)
  if (!customElements.get('details-disclosure')) {
    customElements.define('details-disclosure', class DetailsDisclosure extends HTMLElement {
      constructor() {
        super();
        this.mainDetailsToggle = this.querySelector('details');
        if (!this.mainDetailsToggle) return;
        this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
        this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
        this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
      }
      onFocusOut() {
        setTimeout(() => {
          if (!this.mainDetailsToggle.contains(document.activeElement)) this.close();
        });
      }
      onToggle() {}
      close() {
        this.mainDetailsToggle.removeAttribute('open');
        const summary = this.mainDetailsToggle.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', false);
      }
    });
  }

  // Details modal (for search opened in a modal)
  if (!customElements.get('details-modal')) {
    customElements.define('details-modal', class DetailsModal extends HTMLElement {
      constructor() {
        super();
        this.detailsContainer = this.querySelector('details');
        this.summaryToggle = this.querySelector('summary');
        if (!this.detailsContainer) return;
        this.detailsContainer.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
        if (this.summaryToggle) {
          this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
          this.summaryToggle.setAttribute('role', 'button');
        }
        const closeBtn = this.querySelector('button[type="button"]');
        if (closeBtn) closeBtn.addEventListener('click', this.close.bind(this));
      }
      isOpen() {
        return this.detailsContainer.hasAttribute('open');
      }
      onSummaryClick(event) {
        event.preventDefault();
        event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
      }
      onBodyClick(event) {
        if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) {
          this.close(false);
        }
      }
      open(event) {
        this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
        event.target.closest('details').setAttribute('open', true);
        document.body.addEventListener('click', this.onBodyClickEvent);
        document.body.classList.add('overflow-hidden');
        setTimeout(() => {
          const input = this.detailsContainer.querySelector('input[type="search"]');
          if (input) input.focus();
        }, 50);
      }
      close(focusToggle = true) {
        this.detailsContainer.removeAttribute('open');
        if (this.onBodyClickEvent) document.body.removeEventListener('click', this.onBodyClickEvent);
        document.body.classList.remove('overflow-hidden');
        if (focusToggle && this.summaryToggle) this.summaryToggle.focus();
      }
    });
  }

  // Modal dialog
  if (!customElements.get('modal-dialog')) {
    customElements.define('modal-dialog', class ModalDialog extends HTMLElement {
      constructor() {
        super();
        this.querySelectorAll('[id^="ModalClose-"]').forEach((btn) => btn.addEventListener('click', this.hide.bind(this, false)));
        this.addEventListener('keyup', (event) => {
          if (event.code === 'Escape') this.hide();
        });
      }
      show(opener) {
        this.openedBy = opener;
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
      }
      hide() {
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        if (this.openedBy) try { this.openedBy.focus(); } catch (e) {}
      }
    });
  }

  // Modal opener
  if (!customElements.get('modal-opener')) {
    customElements.define('modal-opener', class ModalOpener extends HTMLElement {
      constructor() {
        super();
        const button = this.querySelector('button');
        if (!button) return;
        button.addEventListener('click', () => {
          const modal = document.querySelector(this.getAttribute('data-modal'));
          if (modal && typeof modal.show === 'function') modal.show(button);
        });
      }
    });
  }
});